import React from 'react';
import styles from './Icon.module.css';
import safeGlob from '../../../utils/safeGlob';

/*
 * Dot icons — 20x20 fixed two-tone status indicators (close, info, questionmark).
 * Colors hardcoded in the SVG source (#CCCCCC ring + #1A212E mark) — these are
 * intentional brand contract per CATALOG notes. No size variant; no color
 * cascade.
 *
 * Uses `?raw` for the same reason as Icon.Standard (visual regression harness Vite
 * doesn't load instance plugins).
 */
const dotModules = safeGlob<string>(
  '../../../assets/icons/dot/*.svg',
  { eager: true, query: '?raw', import: 'default' },
);

const registry: Record<string, string> = {};
for (const [filepath, raw] of Object.entries(dotModules)) {
  const name = filepath.split('/').pop()!.replace(/\.svg$/, '');
  registry[name] = raw;
}

/** Names in the dot registry. */
export type DotIconName = 'close' | 'info' | 'questionmark';

export interface DotProps {
  name: DotIconName;
  /** Optional accessible name (renders role="img" + aria-label); omit for decorative. */
  title?: string;
  className?: string;
}

export const Dot: React.FC<DotProps> = ({ name, title, className }) => {
  const svg = registry[name];

  if (!svg) {
    if (typeof console !== 'undefined') {
      console.warn(`[Icon.Dot] missing asset: dot/${name}.svg`);
    }
    return null;
  }

  const wrapperClass = [styles.icon, className].filter(Boolean).join(' ');
  const a11y = title
    ? { role: 'img' as const, 'aria-label': title }
    : { 'aria-hidden': true as const };

  return (
    <span
      className={wrapperClass}
      {...a11y}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
};

Dot.displayName = 'Icon.Dot';
